import React, { useEffect, useState } from 'react'
import {
  Box,
  Radio,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Grid,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Switch,
  Slider,
  Paper,
  Breadcrumbs,
  Link,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  styled,
  FormHelperText,
  useMediaQuery,
} from '@mui/material'
import { GradientButton, OutlineButton } from '../../components/buttons'
import lightTheme from '../../styles/theme/lightTheme'
import '../../styles/components/_box.scss'
import '../../styles/components/_grids.scss'
import '../../styles/components/_inputs.scss'
import { SelectInputBasic, TextInputClassic } from '../../components/inputs'
import Api from '../../services/api.service'
import { handlingErrors } from '../../utils'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { Slide, toast, ToastContainer } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons'
import { updateUser, userLogout } from '../../store/slices/userAuthReducer'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers'
import {
  AVANTAGES,
  LANGUES,
  ROLE_CANDIDAT,
  ROLE_ENTREPRISE,
  SOFTSKILLS,
  TYPE_CONTRAT,
  TYPE_DEVISE,
  VALEURS,
  niveauLanguesList,
} from '../../data'
import { countries } from '../../data'
import {
  faArrowLeft,
  faClose,
  faSquarePlus,
  faTrash,
  faSave,
  faCheck,
  faExclamationTriangle,
  faArrowRight,
  faEye,
  faBriefcase,
  faBrain,
  faHeart,
  faMapMarkerAlt,
  faEuroSign,
  faHome,
  faBuilding,
  faCalendarAlt,
  faClock,
  faUserTie,
  faUserCheck,
  faRoad,
  faHandshake,
  faChartLine,
  faLanguage,
  faCircleCheck,
  faBan,
} from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import moment from 'moment'

const useStyles = styled((theme) => ({
  root: {
    '& .ql-editor': {
      minHeight: 200,
      backgroundColor: '#f5f5f5',
      border: '1px solid #d1d1d1',
      padding: '10px',
    },
  },
}))

const InfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  borderRadius: '12px',
  marginBottom: '16px',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}))

const SkillChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(99, 102, 241, 0.08)',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  color: '#6366f1',
  fontWeight: 600,
  fontSize: '0.875rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  '&:hover': {
    backgroundColor: 'rgba(99, 102, 241, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const LanguageChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(16, 185, 129, 0.08)',
  border: '1px solid rgba(16, 185, 129, 0.2)',
  color: '#10b981',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(16, 185, 129, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const SoftSkillChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(245, 158, 11, 0.08)',
  border: '1px solid rgba(245, 158, 11, 0.2)',
  color: '#f59e0b',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const ValueChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(139, 92, 246, 0.08)',
  border: '1px solid rgba(139, 92, 246, 0.2)',
  color: '#8b5cf6',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(139, 92, 246, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  width: '100%',
  backgroundColor: lightTheme.palette.primary.b6,
  paddingTop: 0,
  paddingBottom: theme.spacing(4),
  fontFamily: 'Century Gothic',
}))

const ContentContainer = styled(Box)(({ theme }) => ({
  maxWidth: 1200,
  margin: '0 auto',
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
}))

const FormCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: theme.spacing(3),
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  marginBottom: theme.spacing(4),
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid rgba(0,0,0,0.06)',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)',
  },
}))

const FormCardHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${lightTheme.palette.primary.main} 0%, ${lightTheme.palette.primary.dark} 100%)`,
  color: '#ffffff',
  padding: theme.spacing(3, 4),
  fontSize: 18,
  fontFamily: 'Century Gothic Bold',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -10,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderTop: `10px solid ${lightTheme.palette.primary.main}`,
  },
}))

const FormCardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: '#fafbfc',
}))

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  backgroundColor: '#ffffff',
  borderRadius: theme.spacing(2),
  border: `1px solid ${lightTheme.palette.primary.b2}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: lightTheme.palette.primary.main,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
}))

const ListRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: '12px !important',
  width: '100%',
  padding: '12px !important',
  borderRadius: 16,
  backgroundColor: '#f8fafc',
  border: '1px solid rgba(226, 232, 240, 0.9)',
  [theme.breakpoints.up('sm')]: {
    gap: '16px !important',
  },
}))

const FieldWrapper = styled(Box)(() => ({
  flex: '1 1 45%',
  minWidth: 0,
}))

const DeleteIconButton = styled(IconButton)(({ theme }) => ({
  width: 44,
  height: 44,
  alignSelf: 'center',
  borderRadius: 14,
  border: '1px solid rgba(148, 163, 184, 0.5)',
  color: '#475569',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(226, 232, 240, 0.4)',
    borderColor: 'rgba(100, 116, 139, 0.4)',
  },
}))

const AddRowButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  width: '100%',
  padding: '14px 16px',
  borderRadius: 16,
  border: '1px dashed rgba(148, 163, 184, 0.9)',
  backgroundColor: '#ffffff',
  color: '#334155',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8fafc',
    borderColor: 'rgba(99, 102, 241, 0.4)',
  },
  [theme.breakpoints.up('sm')]: {
    padding: '16px 18px',
  },
}))

const StyledFormLabel = styled(Typography)(({ theme }) => ({
  fontSize: { xs: 10, md: 14 },
  fontFamily: 'Century Gothic Bold',
  color: lightTheme.palette.text.primary,
  marginBottom: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}))

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: lightTheme.palette.text.primary,
  boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  borderBottom: `1px solid ${lightTheme.palette.primary.b2}`,
  margin: '0 auto',
}))

const StepIndicator = styled(Box)(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: active
    ? lightTheme.palette.primary.main
    : lightTheme.palette.primary.b3,
  color: active ? '#ffffff' : lightTheme.palette.text.secondary,
  fontSize: 18,
  fontFamily: 'Century Gothic Bold',
  fontWeight: 'bold',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: active
      ? lightTheme.palette.primary.dark
      : lightTheme.palette.primary.b4,
  },
}))

const StepConnector = styled(Box)(({ theme }) => ({
  flex: 1,
  height: 2,
  backgroundColor: lightTheme.palette.primary.b3,
  margin: '0 8px',
  position: 'relative',
  top: -2,
}))

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
}))

const TitleBox = styled(Typography)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 14,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  textTransform: 'uppercase',
  [theme.breakpoints.down('sm')]: {
    fontSize: 12,
  },
}))

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  marginBottom: '24px',
  [theme.breakpoints.down('sm')]: {
    borderRadius: '14px',
  },
}))

const EditBesoinEntreprisePage = () => {
  const userData = useSelector((state) => state.userAuth.user)

  const params = useParams()
  const userId = params.userId
  const userIdProfile = params.userIdProfile
  const besoinId = params.besoinId
  const mode = params.mode

  const role = userData.data.role

  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const proSchema1 = yup.object({
    nbrPoste: yup.number().typeError(t('errorMessage.number')),
    salaire: yup.number().typeError(t('errorMessage.number')),
    /* horaires: yup.string().required(t('errorMessage.required')), */
    descriptifPoste: yup.string().required(t('errorMessage.required')),
  })

  const proSchema = yup.object({
    nbrPoste: yup.number().typeError(t('errorMessage.number')),
    salaire: yup.number().typeError(t('errorMessage.number')),
    /* horaires: yup.string().required(t('errorMessage.required')), */
    descriptifPoste: yup.string().required(t('errorMessage.required')),
    reportDirect: yup.string().required(t('errorMessage.required')),
    teletravailPropose: yup.number().typeError(t('errorMessage.number')),
  })

  const [entrepriseOffres, setEntrepriseOffres] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [loadingPublish, setLoadingPublish] = useState(false)
  const [loadingSuggestion, setLoadingSuggestion] = useState(false)
  const dispatch = useDispatch()
  const [besoin, setBesoin] = useState()

  // Fetch besoin details if route is edit/idBesoin
  useEffect(() => {
    const isEditMode = mode === 'edit'
    console.log(isEditMode, besoinId)
    if (isEditMode && besoinId) {
      const fetchBesoin = async () => {
        try {
          const result = await Api.besoinEntreprise.detail(besoinId)
          const handling = handlingErrors(result)
          if (handling.status === 401) {
            dispatch(userLogout())
            return
          }
          if (handling.ok) {
            setBesoin(result.data)
            setLoadingData(false)
          }
        } catch (error) {
          console.debug('Error fetching besoin details:', error)
        }
      }
      fetchBesoin()
    } else {
      setLoadingData(false)
    }
  }, [mode])

  // Populate form with besoin data when it's fetched
  useEffect(() => {
    if (besoin) {
      setLangues(besoin.besoinLangues || [])
      setCompetences(besoin.competences || [])
      if (besoin.competences.length === 0) {
        setErrorComp(true)
      } else {
        setErrorComp(false)
      }
      setCompetencesAnnexes(besoin.competencesAnnexes || [])
      if (besoin.competencesAnnexes.length === 0) {
        setErrorCompAnn(true)
      } else {
        setErrorCompAnn(false)
      }
      setSoftskils(besoin.softskils || [])
      if (besoin.softskils.length === 0) {
        setErrorSoftskills(true)
      } else {
        setErrorSoftskills(false)
      }
      setValeurs(besoin.valeurs || [])
      if (besoin.valeurs.length === 0) {
        setErrorValeurs(true)
      } else {
        setErrorValeurs(false)
      }
      setAvantagesEntreprise(besoin.avantagesEntreprise || [])
      setTypeContratSelect(
        besoin.typeContrat || TYPE_CONTRAT[i18n.language][0].id
      )
      setTypeDeviseSelect(besoin.devise || TYPE_DEVISE[0].id)
      setSelectedTypeValue(besoin.type || t('box.jobReplacement'))
      setSelectedSalaireSur(besoin.salaireSur || 12)
      setDeplacementValue(besoin.deplacement || false)
      setPermisValue(besoin.permis || false)
      setVehiculeValue(besoin.vehicule || false)
      setTempPartiel(besoin.tempPartiel || MIN)
      setSelectedTypeTempsTravail(besoin.typeTempsTravail || 'plein')
      setPosteSelect(
        besoin.poste || { id: 0, titre: t('box.jobTitle'), titreEn: '' }
      )
      setSelectedCountry(besoin.pays ? { label: besoin.pays } : countries[0])
      setSelectedCity(besoin.lieu || '')
      setOfferSelect(besoin.entrepriseOffre || null)
      setVisible(besoin.visible !== undefined ? besoin.visible : true)

      if (besoin.dateDebut) {
        setDateValue(dayjs(besoin.dateDebut))
      }
      if (besoin.date) {
        setDateValuePubli(dayjs(besoin.date))
      }
      if (besoin.descriptifPoste) {
        setEditorHtml(besoin.descriptifPoste)
      }
      if (besoin.dateExpiration) {
        setDateExpiration(
          dayjs(besoin.dateExpiration).format('DD/MM/YYYY HH:mm')
        )
      }

      // Reset form with besoin data
      const formData = {
        nbrPoste: besoin.nbrPoste || '',
        salaire: besoin.salaire || '',
        horaires: besoin.horaires || '',
        descriptifPoste: besoin.descriptifPoste || '',
        reportDirect: besoin.reportDirect || '',
        teletravailPropose: besoin.teletravailPropose || 0,
      }
      reset(formData)
    }
  }, [besoin])

  const [langues, setLangues] = useState([])
  const [refresh, setRefresh] = useState(1)
  const [competences, setCompetences] = useState([])
  const [input, setInput] = useState('')
  const [inputCompetence, setInputCompetence] = useState('')
  const [competencesAnnexes, setCompetencesAnnexes] = useState([])
  const [softskils, setSoftskils] = useState([])
  const [valeurs, setValeurs] = useState([])
  const [avantagesEntreprise, setAvantagesEntreprise] = useState([])
  const [typeContratSelect, setTypeContratSelect] = useState(
    TYPE_CONTRAT[i18n.language][0].id
  )
  const [typeDeviseSelect, setTypeDeviseSelect] = useState(TYPE_DEVISE[0].id)
  const [selectedTypeValue, setSelectedTypeValue] = useState(
    t('box.jobReplacement')
  )
  const [selectedSalaireSur, setSelectedSalaireSur] = useState(12)
  const [deplacementValue, setDeplacementValue] = useState(false)
  const [permisValue, setPermisValue] = useState(false)
  const [vehiculeValue, setVehiculeValue] = useState(false)
  const [loadingPoste, setLoadingPoste] = useState(false)
  const [postes, setPostes] = useState([])
  const [posteSelect, setPosteSelect] = useState({
    id: 0,
    titre: t('box.jobTitle'),
    titreEn: '',
  })

  const [offerSelect, setOfferSelect] = useState(null)
  const [posteOpen, setPosteOpen] = useState(false)
  const [offerOpen, setOfferOpen] = useState(false)
  const [dateValue, setDateValue] = useState(dayjs(new Date()))
  const [dateValuePubli, setDateValuePubli] = useState(dayjs(new Date()))
  const [dateExpiration, setDateExpiration] = useState('')
  const [step, setStep] = useState(1)
  const [selectedCountry, setSelectedCountry] = useState({
    label: 'Switzerland',
  })
  const [cities, setCities] = useState([])
  const [selectedCity, setSelectedCity] = useState('Genève')
  const [selectedTypeTempsTravail, setSelectedTypeTempsTravail] =
    useState('plein')
  const [visible, setVisible] = useState(true)

  const MAX = 100
  const MIN = 20
  const marks = [
    { value: MIN, label: MIN + '%' },
    { value: MIN + 10, label: '' },
    { value: MIN + 20, label: MIN + 20 + '%' },
    { value: MIN + 30, label: '' },
    { value: MIN + 40, label: MIN + 40 + '%' },
    { value: MIN + 50, label: '' },
    { value: MIN + 60, label: MIN + 60 + '%' },
    { value: MIN + 70, label: '' },
    { value: MAX, label: MAX + '%' },
  ]
  const [tempPartiel, setTempPartiel] = useState(
    besoin ? besoin.tempPartiel : MIN
  )
  const [errorPoste, setErrorPoste] = useState(false)
  const [errorComp, setErrorComp] = useState(
    competences.length === 0 ? true : false
  )
  const [errorCompAnn, setErrorCompAnn] = useState(
    competencesAnnexes.length === 0 ? true : false
  )
  const [errorSoftskills, setErrorSoftskills] = useState(
    softskils.length < 6 ? true : false
  )
  const [errorValeurs, setErrorValeurs] = useState(
    valeurs.length < 6 ? true : false
  )

  const classes = useStyles()
  const [editorHtml, setEditorHtml] = useState(
    besoin ? besoin.descriptifPoste : ''
  )

  let typingTimer
  let doneTypingInterval = 800

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      nbrPoste: besoin ? besoin.nbrPoste : '',
      horaires: besoin ? besoin.horaires : '',
      lieu: besoin ? besoin.lieu : '',
      salaire: besoin ? besoin.salaire : '',
      pays: besoin ? besoin.pays : '',
      descriptifPoste: besoin ? besoin.descriptifPoste : '',
      teletravailPropose: besoin ? besoin.teletravailPropose : '',
      reportDirect: besoin ? besoin.reportDirect : '',
    },
    resolver: yupResolver(step === 1 ? proSchema1 : proSchema),
  })

  const steps = [
    t('box.step1Title'),
    t('box.step2Title'),
    t('box.step3Title'),
    t('box.step5Title'),
    t('box.step4Title'),
  ]

  useEffect(() => {
    handleCountryChange(selectedCountry, besoin)
    getPostes()
    getCompanySubscriptions()
  }, [])

  const handleCountryChange = async (country, besoin = null) => {
    setSelectedCountry(country)

    try {
      const result = await Api.countriesCities.getCitiesByCountry(country.label)
      const res = handlingErrors(result)

      if (res.status === 401) {
        toast.error(t('successMessage.sessionExpired'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
      }

      if (res.ok) {
        setCities(res.data.data)
        if (!besoin) setSelectedCity(res.data.data[0])
      }
    } catch (error) {
      console.debug(error)
    }
  }

  const getPostes = async (searchQuery) => {
    let payload = { search: searchQuery }
    payload = Object.entries(payload).reduce(
      (a, [k, v]) => (v ? ((a[k] = v), a) : a),
      {}
    )
    setLoadingPoste(true)

    try {
      const result = await Api.poste.listWithFilter({
        status: 1,
        ...payload,
      })

      const res = handlingErrors(result)

      if (res.status === 401) {
        toast.error(t('successMessage.sessionExpired'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
      }

      if (res.ok) {
        setPostes(res.data)
        res.data.sort((a, b) => (a.date < b.date ? 1 : -1))
        if (besoin) {
          setPosteSelect(besoin.poste)
        }
        if (posteSelect.id === 0) {
          setPosteSelect(res.data[0])
        }
      }

      if (res.status === 0) {
        toast.error(t('successMessage.somethingWrong'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
      }
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        icon: ({ theme, type }) => (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            fontSize={25}
            color={lightTheme.palette.error.main}
          />
        ),
      })
    }
    setLoadingPoste(false)
  }

  const handleChangeDeplacement = (event) => {
    setDeplacementValue(!deplacementValue)
  }

  const handleChangeVisible = (event) => {
    setVisible(!visible)
  }

  const handleChangePermis = (event) => {
    setPermisValue(!permisValue)
  }

  const handleChangeVehicule = (event) => {
    setVehiculeValue(!vehiculeValue)
  }

  const handleChangeType = (event) => {
    const inputValue = event.target.value
    setSelectedTypeValue(inputValue)
  }

  const handleChangeSalaireSur = (event) => {
    const inputValue = event.target.value
    setSelectedSalaireSur(parseInt(inputValue))
  }

  const handleTypeContratSelect = (event) => {
    const inputValue = event.target.value
    setTypeContratSelect(inputValue)
  }

  const handleTypeDeviseSelect = (event) => {
    const inputValue = event.target.value
    setTypeDeviseSelect(inputValue)
  }

  const handleChangeTypeTempsTravail = (event) => {
    const inputValue = event.target.value
    setSelectedTypeTempsTravail(inputValue)
  }

  const changeDate = (dateValue) => {
    setDateValue(dateValue)
  }

  const changeDatePubli = (dateValue) => {
    setDateValuePubli(dateValue)
  }

  const handleChangeTime = (_, newValue) => {
    setTempPartiel(newValue)
  }

  const handleChange = (html) => {
    const cleanedContent = html.trim()

    if (cleanedContent !== '') {
      setEditorHtml(html)
      setValue('descriptifPoste', html.replace(/<p><br><\/p>/g, '<p></p>'))
    } else {
      setEditorHtml('')
      setValue('descriptifPoste', '')
    }
  }

  const nextStep = () => {
    setStep(step + 1)
  }

  const previousStep = () => {
    setStep(step - 1)
  }

  const firstStep = () => {
    setStep(1)
  }

  const handlePublish = async () => {
    setStep(5)
  }

  const getCompanySubscriptions = async () => {
    try {
      let result = await Api.companySubscriptions.getCompanySubscriptions({
        utilisateur: `${userId ? userId : userData?.data.id}`,
      })

      const handling = handlingErrors(result)

      if (handling.ok) {
        setEntrepriseOffres(result.data.data.offres)
        dispatch(
          updateUser({
            data: {
              offres: result.data.data.offres,
              allowedPermissions: result.data.data.allowedPermissions,
              autorise: result.data.data.autorise,
            },
          })
        )
      } else {
        console.log(handling)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const handleSubmitPublish = async () => {
    if (!offerSelect) {
      return
    }
    setLoadingPublish(true)

    try {
      const result = await Api.besoinEntreprise.edit(besoin.id, {
        companySubscription: '/api/company_subscriptions/' + offerSelect.id,
      })
      const handling = handlingErrors(result)
      if (handling.status === 401) {
        dispatch(userLogout())
        return
      }

      if (handling.ok) {
        setBesoin(result.data)
        setLoadingPublish(false)
        toast.success(t('successMessage.published'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        })
        getCompanySubscriptions()
      } else {
        setLoadingPublish(false)
      }
    } catch (error) {
      console.debug('erreur submit !')
    }
  }

  const onSubmit1 = async () => {
    if (step === 1 && !posteSelect.id) {
      setErrorPoste(true)
      return
    }
    if (
      step === 2 &&
      (errorComp || errorCompAnn || errorSoftskills || errorValeurs)
    ) {
      return
    }
    nextStep()
  }

  const onSubmit = async (data) => {
    setLoading(true)
    let datas = {
      entreprise: userIdProfile
        ? '/api/entreprises/' + userIdProfile
        : '/api/entreprises/' + userData.data.idProfile,
      dateDebut: dateValue,
      date: dateValuePubli,
      typeContrat: typeContratSelect,
      devise: typeDeviseSelect,
      poste: '/api/postes/' + posteSelect.id,
      pays: selectedCountry.label,
      lieu: selectedCity,
      type: selectedTypeValue,
      salaireSur: selectedSalaireSur,
      besoinLangues: langues,
      langues: langues.map((item) => item.langue),
      deplacement: deplacementValue,
      permisRequis: permisValue,
      vehiculeRequis: vehiculeValue,
      competences: competences,
      competencesAnnexes: competencesAnnexes,
      softskils: softskils,
      valeurs: valeurs,
      avantagesEntreprise: avantagesEntreprise,
      typeTempsTravail: selectedTypeTempsTravail,
      tempPartiel: tempPartiel,
      visible: visible,
      ...data,
    }

    datas.lieu = selectedCity
    datas.pays = selectedCountry.label

    //console.log(datas, besoin)
    //return

    if (besoin) {
      try {
        const result = await Api.besoinEntreprise.edit(besoin.id, datas)
        const handling = handlingErrors(result)
        if (handling.status === 401) {
          dispatch(userLogout())
          return
        }

        if (handling.ok) {
          setLoading(false)
          toast.success(t('successMessage.updated'), {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          })
        } else {
          setLoading(false)
        }
      } catch (error) {
        console.debug('erreur submit !')
      }
    } else {
      try {
        const result = await Api.besoinEntreprise.add(datas, {})
        const handling = handlingErrors(result)
        if (handling.status === 401) {
          dispatch(userLogout())
          return
        }

        if (handling.ok) {
          setLoading(false)
          setBesoin(handling.data)
          toast.success(t('successMessage.created'), {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          })
        } else {
          setLoading(false)
        }
      } catch (error) {
        console.debug('erreur submit !')
      }
    }
  }

  const handleAddLangue = () => {
    const lng = { langue: LANGUES[24], niveau: niveauLanguesList[0].id }
    langues.push(lng)
    setLangues(langues)
    setRefresh(Math.random())
  }

  const handleSuggesstion = async () => {
    setLoadingSuggestion(true)
    try {
      const result = await Api.besoinEntreprise.suggestionIA(
        posteSelect.titre,
        selectedCity,
        selectedCountry.label,
        i18n.language,
        typeDeviseSelect
      )

      const handling = handlingErrors(result)

      if (handling.status === 401) {
        console.log('🚪 Session expirée - déconnexion')
        dispatch(userLogout())
        return
      }

      if (handling.ok) {
        setValue('descriptifPoste', handling.data.data.description)
        setEditorHtml(handling.data.data.description)
        setValue('salaire', handling.data.data.salary_range)
        setCompetences([...handling.data.data.key_skills])
        setAvantagesEntreprise([...handling.data.data.benefits])

        // Afficher un toast de succès
        toast.success('Suggestion générée avec succès !', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          newestOnTop: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleCheck}
              fontSize={25}
              color={lightTheme.palette.success.main}
            />
          ),
        })
        setLoadingSuggestion(false)
      } else {
        console.log('❌ Erreur - statut:', handling.status)
        // Afficher un toast d'erreur
        toast.error('Erreur lors de la génération de la suggestion', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          newestOnTop: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
      }
    } catch (error) {
      console.error('💥 Erreur catchée:', error)
      toast.error('Erreur réseau lors de la suggestion', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        newestOnTop: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        icon: ({ theme, type }) => (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            fontSize={25}
            color={lightTheme.palette.error.main}
          />
        ),
      })
    } finally {
      setLoadingSuggestion(false)
    }
  }
  const sizeChip = useMediaQuery(lightTheme.breakpoints.down('sm'))
    ? 'small'
    : 'medium'

  const isMobile = useMediaQuery(lightTheme.breakpoints.down('sm'))

  return (
    <PageContainer>
      <StyledAppBar position="sticky">
        <Toolbar
          sx={{
            minHeight: 64,
            width: { xs: '100%', lg: 1200 },
            margin: '0 auto',
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{
              mr: 2,
              backgroundColor: lightTheme.palette.primary.b6,
              borderRadius: 1,
              padding: 1,
              '&:hover': {
                backgroundColor: lightTheme.palette.primary.b4,
              },
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontFamily: 'Century Gothic Bold',
              fontSize: 18,
              color: lightTheme.palette.text.primary,
            }}
          >
            {besoin ? t('box.title.editJob') : t('box.title.addJob')}
          </Typography>
        </Toolbar>
      </StyledAppBar>

      <ContentContainer>
        {loadingData ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
          <Link color="inherit" href="#" onClick={() => navigate('/admin')}>
            {t('navigation.dashboard')}
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/admin/jobs')}
          >
            {t('navigation.jobs')}
          </Link>
          <Typography color="text.primary">
            {besoin ? t('box.title.editJob') : t('box.title.addJob')}
          </Typography>
        </Breadcrumbs> */}

            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 4,
                }}
              >
                {steps.map((label, index) => (
                  <React.Fragment key={label}>
                    <StepIndicator
                      active={index < step}
                      onClick={() => index < step && setStep(index + 1)}
                    >
                      {index + 1}
                    </StepIndicator>
                    {index < steps.length - 1 && <StepConnector />}
                  </React.Fragment>
                ))}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 2,
                }}
              >
                {steps.map((label, index) => (
                  <Typography
                    key={`label-${label}`}
                    variant="body2"
                    sx={{
                      fontSize: 12,
                      fontFamily: 'Century Gothic',
                      color:
                        index < step
                          ? lightTheme.palette.primary.main
                          : lightTheme.palette.primary.b3,
                      fontWeight: index < step ? 'bold' : 'normal',
                      textAlign: 'center',
                      minWidth: 60,
                      maxWidth: 100,
                      flex: 1,
                    }}
                  >
                    {label}
                  </Typography>
                ))}
              </Box>
            </Box>

            <form>
              {/* Step 1: Basic Information */}
              <FormCard sx={{ display: step === 1 ? 'block' : 'none' }}>
                <FormCardHeader>{t('box.step1Title')}</FormCardHeader>
                <FormCardContent>
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      md={
                        role !== ROLE_ENTREPRISE && role !== ROLE_CANDIDAT
                          ? 6
                          : 12
                      }
                    >
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.visible')}
                          <Box sx={{ marginLeft: 'auto' }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  onChange={handleChangeVisible}
                                  defaultChecked={visible}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: lightTheme.palette.primary.main,
                                    },
                                    '& .MuiSwitch-track': {
                                      backgroundColor:
                                        lightTheme.palette.primary.b3,
                                    },
                                  }}
                                />
                              }
                              label=""
                            />
                          </Box>
                        </StyledFormLabel>
                      </FormSection>
                    </Grid>

                    {role !== ROLE_ENTREPRISE && role !== ROLE_CANDIDAT && (
                      <Grid item xs={12} md={6}>
                        <FormSection>
                          <StyledFormLabel>
                            {t('box.publicationDate')}
                            <FontAwesomeIcon
                              icon={faExclamationTriangle}
                              style={{
                                color: lightTheme.palette.error.main,
                                fontSize: 14,
                              }}
                            />
                          </StyledFormLabel>
                          <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale={'fr'}
                          >
                            <DateTimePicker
                              onChange={changeDatePubli}
                              value={dateValuePubli}
                              inputFormat={'DD/MM/YYYY HH:mm'}
                              renderInput={({
                                inputRef,
                                inputProps,
                                InputProps,
                              }) => (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    height: 45,
                                    backgroundColor:
                                      lightTheme.palette.primary.b6,
                                    borderRadius: '8px',
                                  }}
                                >
                                  <input
                                    ref={inputRef}
                                    {...inputProps}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      borderRadius: '8px',
                                      border: '1px solid #e0e0e0',
                                      padding: '10px 15px',
                                      fontFamily: 'Century Gothic',
                                      fontSize: 14,
                                      lineHeight: '140%',
                                      letterSpacing: '0.2px',
                                      color: lightTheme.palette.text.primary,
                                      backgroundColor: '#FFFFFF',
                                    }}
                                  />
                                  {InputProps?.endAdornment}
                                </Box>
                              )}
                            />
                          </LocalizationProvider>
                        </FormSection>
                      </Grid>
                    )}

                    <Grid item xs={12} md={4}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobTitle')}
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{
                              color: lightTheme.palette.error.main,
                              fontSize: 14,
                            }}
                          />
                        </StyledFormLabel>
                        <Autocomplete
                          disableClearable
                          value={posteSelect ? posteSelect : { titre: '' }}
                          id="asynchronous-postes"
                          sx={{ width: '100%', height: '45px' }}
                          open={posteOpen}
                          onChange={(event, newValue) => {
                            setPosteSelect(newValue)
                            setErrorPoste(false)
                          }}
                          onKeyDown={(event, newInputValue) => {
                            clearTimeout(typingTimer)
                            if (event.target.value) {
                              typingTimer = setTimeout(function () {
                                getPostes(event.target.value)
                              }, doneTypingInterval)
                            }
                          }}
                          onOpen={() => setPosteOpen(true)}
                          onClose={() => setPosteOpen(false)}
                          isOptionEqualToValue={(option, value) =>
                            option.titre === value.titre
                          }
                          getOptionLabel={(option) =>
                            role !== ROLE_ENTREPRISE &&
                            role !== ROLE_CANDIDAT &&
                            option.titreEn
                              ? option.titre + ' (' + option.titreEn + ')'
                              : i18n.language === 'fr'
                              ? option.titre
                              : option.titreEn
                          }
                          options={postes}
                          loading={loadingPoste}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{ backgroundColor: '#fff' }}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <React.Fragment>
                                    {loadingPoste ? (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </React.Fragment>
                                ),
                              }}
                            />
                          )}
                        />
                      </FormSection>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobNumber')}
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{
                              color: lightTheme.palette.error.main,
                              fontSize: 14,
                            }}
                          />
                        </StyledFormLabel>
                        <TextInputClassic
                          placeholder={t('box.jobNumber')}
                          type={'text'}
                          register={{ ...register('nbrPoste') }}
                          width={'100%'}
                        />
                        {errors.nbrPoste && (
                          <Typography className={'error-message'}>
                            {errors.nbrPoste.message}
                          </Typography>
                        )}
                      </FormSection>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobContractType')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <SelectInputBasic
                          type={'border'}
                          items={TYPE_CONTRAT[i18n.language]}
                          value={typeContratSelect}
                          handleChange={handleTypeContratSelect}
                        />
                      </FormSection>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobPositionType')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <RadioGroup
                          row
                          aria-labelledby="demo-row-radio-buttons-group-label"
                          name="row-radio-buttons-group"
                        >
                          <FormControlLabel
                            onChange={handleChangeType}
                            checked={
                              selectedTypeValue === 'Remplacement de poste'
                            }
                            value="Remplacement de poste"
                            control={<Radio />}
                            label={t('box.jobReplacement')}
                          />
                          <FormControlLabel
                            onChange={handleChangeType}
                            checked={selectedTypeValue === 'Création de poste'}
                            value="Création de poste"
                            control={<Radio />}
                            label={t('box.jobCreation')}
                          />
                        </RadioGroup>
                      </FormSection>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobExpectedStartDate')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale={'fr'}
                        >
                          <DatePicker
                            onChange={changeDate}
                            value={dateValue}
                            inputFormat={'DD/MM/YYYY'}
                            renderInput={({
                              inputRef,
                              inputProps,
                              InputProps,
                            }) => (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '100%',
                                  height: 45,
                                  backgroundColor:
                                    lightTheme.palette.primary.b6,
                                  borderRadius: '8px',
                                }}
                              >
                                <input
                                  ref={inputRef}
                                  {...inputProps}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0',
                                    padding: '10px 15px',
                                    fontFamily: 'Century Gothic',
                                    fontSize: 14,
                                    lineHeight: '140%',
                                    letterSpacing: '0.2px',
                                    color: lightTheme.palette.text.primary,
                                    backgroundColor: '#FFFFFF',
                                  }}
                                />
                                {InputProps?.endAdornment}
                              </Box>
                            )}
                          />
                        </LocalizationProvider>
                      </FormSection>
                    </Grid>

                    {/* <Grid item xs={12} md={6}>
                  <FormSection>
                    <StyledFormLabel>
                      {t('box.jobPositionHours')}
                      <span style={{ color: lightTheme.palette.error.main }}>
                        *
                      </span>
                    </StyledFormLabel>
                    <TextInputClassic
                      placeholder={'Ex : 9h-16h'}
                      type={'text'}
                      value={besoin ? besoin.horaires : undefined}
                      register={{ ...register('horaires') }}
                      width={'100%'}
                    />
                    {errors.horaires && (
                      <Typography className={'error-message'}>
                        {errors.horaires.message}
                      </Typography>
                    )}
                  </FormSection>
                </Grid> */}

                    <Grid item xs={12} md={3}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobSalary')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <TextInputClassic
                          placeholder={'Ex : 10000'}
                          type={'text'}
                          register={{ ...register('salaire') }}
                          width={'100%'}
                        />
                        {errors.salaire && (
                          <Typography className={'error-message'}>
                            {errors.salaire.message}
                          </Typography>
                        )}
                      </FormSection>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobCurrency')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <SelectInputBasic
                          type={'border'}
                          items={TYPE_DEVISE}
                          value={typeDeviseSelect}
                          handleChange={handleTypeDeviseSelect}
                        />
                      </FormSection>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobSalaryOn')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <RadioGroup
                          row
                          aria-labelledby="demo-row-radio-buttons-group-label"
                          name="row-radio-buttons-group"
                        >
                          <FormControlLabel
                            onChange={handleChangeSalaireSur}
                            checked={selectedSalaireSur === 12}
                            value="12"
                            control={<Radio />}
                            label={'12 ' + t('box.mounth')}
                          />
                          <FormControlLabel
                            onChange={handleChangeSalaireSur}
                            checked={selectedSalaireSur === 13}
                            value="13"
                            control={<Radio />}
                            label={'13 ' + t('box.mounth')}
                          />
                          <FormControlLabel
                            onChange={handleChangeSalaireSur}
                            checked={selectedSalaireSur === 14}
                            value="14"
                            control={<Radio />}
                            label={'14 ' + t('box.mounth')}
                          />
                        </RadioGroup>
                      </FormSection>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobCountry')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <Autocomplete
                          sx={{
                            width: '100%',
                            backgroundColor: '#fff',
                          }}
                          value={
                            selectedCountry ? selectedCountry : { label: '' }
                          }
                          onChange={(event, newValue) => {
                            handleCountryChange(newValue)
                          }}
                          id="country-select"
                          options={countries}
                          autoHighlight
                          getOptionLabel={(option) => option.label}
                          renderOption={(props, option) => (
                            <Box
                              component="li"
                              sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                              {...props}
                            >
                              <img
                                loading="lazy"
                                width="20"
                                src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                alt=""
                              />
                              {option.label} ({option.code})
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              inputProps={{
                                ...params.inputProps,
                              }}
                            />
                          )}
                        />
                      </FormSection>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobLocation')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <Autocomplete
                          sx={{
                            width: '100%',
                            backgroundColor: '#fff',
                          }}
                          value={selectedCity ? selectedCity : ''}
                          freeSolo={true}
                          onChange={(event, newValue) => {
                            setSelectedCity(newValue)
                          }}
                          id="city-select"
                          options={cities}
                          autoHighlight
                          renderOption={(props, option) => (
                            <Box
                              component="li"
                              sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                              {...props}
                            >
                              {option}
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              onChange={(event) => {
                                setSelectedCity(event.target.value)
                              }}
                              {...params}
                              inputProps={{
                                ...params.inputProps,
                              }}
                            />
                          )}
                        />
                      </FormSection>
                    </Grid>

                    <Grid item xs={12}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobDescription')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <div
                          className={classes.root}
                          style={{ backgroundColor: '#fff' }}
                        >
                          <ReactQuill
                            value={editorHtml}
                            onChange={handleChange}
                            backgroundColor={'#fff'}
                          />
                        </div>
                        {errors.descriptifPoste && (
                          <Typography className={'error-message'}>
                            {errors.descriptifPoste.message}
                          </Typography>
                        )}
                      </FormSection>
                    </Grid>
                  </Grid>
                </FormCardContent>
              </FormCard>

              {/* Step 2: Additional Information */}
              <FormCard sx={{ display: step === 2 ? 'block' : 'none' }}>
                <FormCardHeader>{t('box.step2Title')}</FormCardHeader>
                <FormCardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobTelework')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <TextInputClassic
                          placeholder={'0'}
                          type={'text'}
                          register={{ ...register('teletravailPropose') }}
                          width={'100%'}
                        />
                        {errors.teletravailPropose && (
                          <Typography className={'error-message'}>
                            {errors.teletravailPropose.message}
                          </Typography>
                        )}
                      </FormSection>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.timeWorkType')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <RadioGroup
                          row
                          aria-labelledby="demo-row-radio-buttons-group-label"
                          name="row-radio-buttons-group"
                        >
                          <FormControlLabel
                            onChange={handleChangeTypeTempsTravail}
                            checked={selectedTypeTempsTravail === 'plein'}
                            value="plein"
                            control={<Radio />}
                            label={t('box.timeWorkType1')}
                          />
                          <FormControlLabel
                            onChange={handleChangeTypeTempsTravail}
                            checked={selectedTypeTempsTravail === 'partiel'}
                            value="partiel"
                            control={<Radio />}
                            label={t('box.timeWorkType2')}
                          />
                        </RadioGroup>
                      </FormSection>
                    </Grid>

                    {selectedTypeTempsTravail === 'partiel' && (
                      <Grid item xs={12} md={12}>
                        <FormSection>
                          <StyledFormLabel>
                            {t('box.timeWorkType2')}
                            <span
                              style={{ color: lightTheme.palette.error.main }}
                            >
                              *
                            </span>
                          </StyledFormLabel>
                          <Box sx={{ width: '90%', margin: '0' }}>
                            <Slider
                              marks={marks}
                              step={10}
                              value={tempPartiel}
                              valueLabelDisplay="auto"
                              min={MIN}
                              max={MAX}
                              onChange={handleChangeTime}
                            />
                          </Box>
                        </FormSection>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobReport')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <TextInputClassic
                          placeholder={''}
                          type={'text'}
                          register={{ ...register('reportDirect') }}
                          width={'100%'}
                        />
                        {errors.reportDirect && (
                          <Typography className={'error-message'}>
                            {errors.reportDirect.message}
                          </Typography>
                        )}
                      </FormSection>
                    </Grid>

                    <Grid item xs={12}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('filter.competences')} (max 6)
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        {isMobile ? (
                          <>
                            <TextInputClassic
                              type="text"
                              width="100%"
                              value={inputCompetence}
                              onChange={(e) =>
                                setInputCompetence(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (
                                  e.key === 'Enter' &&
                                  inputCompetence.trim()
                                ) {
                                  setCompetences([
                                    ...competences,
                                    inputCompetence.trim(),
                                  ])
                                  setInputCompetence('')
                                }
                              }}
                            />

                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                mt: 1,
                              }}
                            >
                              {competences.map((competence, index) => (
                                <Chip
                                  key={index}
                                  variant="outlined"
                                  label={competence}
                                  onDelete={() => {
                                    setCompetences(
                                      competences.filter((_, i) => i !== index)
                                    )
                                  }}
                                />
                              ))}
                            </Box>
                          </>
                        ) : (
                          <Autocomplete
                            sx={{ width: '100%', backgroundColor: '#fff' }}
                            multiple
                            value={competences}
                            options={[]}
                            onChange={(event, newValue) => {
                              if (newValue.length > 6) newValue.slice(0, 6)

                              if (newValue.length === 0) {
                                setErrorComp(true)
                              } else {
                                setErrorComp(false)
                              }
                              setCompetences(newValue.slice(0, 6))
                            }}
                            freeSolo
                            renderTags={(competences, getTagProps) =>
                              competences
                                .slice(0, 6)
                                .map((competence, index) => (
                                  <Chip
                                    variant="outlined"
                                    label={competence}
                                    {...getTagProps({ index })}
                                  />
                                ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={t(
                                  'filter.competencesTextCandidat'
                                )}
                              />
                            )}
                          />
                        )}
                        {errorComp && (
                          <Typography className={'error-message'}>
                            {t('errorMessage.listComp')}
                          </Typography>
                        )}
                      </FormSection>
                    </Grid>

                    <Grid item xs={12}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('filter.competencesAnnexes')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        {isMobile ? (
                          <>
                            <TextInputClassic
                              type="text"
                              width="100%"
                              value={input}
                              onChange={(e) => setInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && input.trim()) {
                                  setCompetencesAnnexes([
                                    ...competencesAnnexes,
                                    input.trim(),
                                  ])
                                  setInput('')
                                }
                              }}
                            />

                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                mt: 1,
                              }}
                            >
                              {competencesAnnexes.map((competence, index) => (
                                <Chip
                                  key={index}
                                  variant="outlined"
                                  label={competence}
                                  onDelete={() => {
                                    setCompetencesAnnexes(
                                      competencesAnnexes.filter(
                                        (_, i) => i !== index
                                      )
                                    )
                                  }}
                                />
                              ))}
                            </Box>
                          </>
                        ) : (
                          <Autocomplete
                            sx={{ width: '100%', backgroundColor: '#fff' }}
                            multiple
                            value={competencesAnnexes}
                            options={[]}
                            onChange={(event, newValue) => {
                              if (newValue.length === 0) {
                                setErrorCompAnn(true)
                              } else {
                                setErrorCompAnn(false)
                              }
                              setCompetencesAnnexes(newValue)
                            }}
                            freeSolo
                            renderTags={(competencesAnnexes, getTagProps) =>
                              competencesAnnexes.map(
                                (competenceAnnexe, index) => (
                                  <Chip
                                    variant="outlined"
                                    label={competenceAnnexe}
                                    {...getTagProps({ index })}
                                  />
                                )
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={t(
                                  'filter.competencesTextCandidat'
                                )}
                              />
                            )}
                          />
                        )}
                        {errorCompAnn && (
                          <Typography className={'error-message'}>
                            {t('errorMessage.listCompAnn')}
                          </Typography>
                        )}
                      </FormSection>
                    </Grid>

                    <Grid item xs={12}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('filter.softskils')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <Autocomplete
                          sx={{ width: '100%', backgroundColor: '#fff' }}
                          multiple
                          value={softskils}
                          options={[
                            ...SOFTSKILLS['fr'],
                            ...SOFTSKILLS['en'],
                          ].sort()}
                          onChange={(event, newValue) => {
                            if (newValue.length < 6) {
                              setErrorSoftskills(true)
                            } else {
                              setErrorSoftskills(false)
                            }
                            setSoftskils(newValue)
                          }}
                          renderTags={(softskils, getTagProps) =>
                            softskils.map((softskill, index) => (
                              <Chip
                                variant="outlined"
                                label={softskill}
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField {...params} placeholder={' '} />
                          )}
                        />
                        {errorSoftskills && (
                          <Typography className={'error-message'}>
                            {t('errorMessage.listCompSoftskills')}
                          </Typography>
                        )}
                      </FormSection>
                    </Grid>

                    <Grid item xs={12}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('filter.values')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <Autocomplete
                          sx={{ width: '100%', backgroundColor: '#fff' }}
                          multiple
                          value={valeurs}
                          options={[...VALEURS['fr'], ...VALEURS['en']]}
                          onChange={(event, newValue) => {
                            if (newValue.length < 6) {
                              setErrorValeurs(true)
                            } else {
                              setErrorValeurs(false)
                            }
                            setValeurs(newValue)
                          }}
                          renderTags={(valeurs, getTagProps) =>
                            valeurs.map((valeur, index) => (
                              <Chip
                                variant="outlined"
                                label={valeur}
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField {...params} placeholder={' '} />
                          )}
                        />
                        {errorValeurs && (
                          <Typography className={'error-message'}>
                            {t('errorMessage.list')}
                          </Typography>
                        )}
                      </FormSection>
                    </Grid>

                    <Grid item xs={12}>
                      <FormSection>
                        <StyledFormLabel>
                          {t('box.jobBenefits')}
                          <span
                            style={{ color: lightTheme.palette.error.main }}
                          >
                            *
                          </span>
                        </StyledFormLabel>
                        <Autocomplete
                          sx={{ width: '100%', backgroundColor: '#fff' }}
                          multiple
                          freeSolo
                          value={avantagesEntreprise}
                          options={[...AVANTAGES['fr'], ...AVANTAGES['en']]}
                          onChange={(event, newValue) => {
                            setAvantagesEntreprise(newValue)
                          }}
                          renderTags={(avantagesEntreprise, getTagProps) =>
                            avantagesEntreprise.map((avantage, index) => (
                              <Chip
                                variant="outlined"
                                label={avantage}
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField {...params} placeholder={' '} />
                          )}
                        />
                      </FormSection>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormSection>
                        <FormControlLabel
                          control={
                            <Switch
                              onChange={handleChangeDeplacement}
                              defaultChecked={deplacementValue}
                            />
                          }
                          label={t('box.jobTrip')}
                        />
                      </FormSection>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormSection>
                        <FormControlLabel
                          control={
                            <Switch
                              onChange={handleChangePermis}
                              defaultChecked={permisValue}
                            />
                          }
                          label={t('box.jobPermit')}
                        />
                      </FormSection>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormSection>
                        <FormControlLabel
                          control={
                            <Switch
                              onChange={handleChangeVehicule}
                              defaultChecked={vehiculeValue}
                            />
                          }
                          label={t('box.jobVehicle')}
                        />
                      </FormSection>
                    </Grid>
                  </Grid>
                </FormCardContent>
              </FormCard>

              {/* Step 3: Languages */}
              <FormCard sx={{ display: step === 3 ? 'block' : 'none' }}>
                <FormCardHeader>{t('box.step3Title')}</FormCardHeader>
                <FormCardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <StyledFormLabel>{t('filter.languages')}</StyledFormLabel>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        {langues.map((lng, index) => (
                          <ListRow key={`langue-${index}`}>
                            <FieldWrapper
                              sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%' } }}
                            >
                              <Autocomplete
                                sx={{
                                  width: '100%',
                                  minWidth: 0,
                                  height: 45,
                                }}
                                options={LANGUES}
                                value={lng.langue}
                                freeSolo
                                onChange={(event, newValue) => {
                                  const newLangues = [...langues]
                                  newLangues[index].langue = newValue
                                  setLangues(newLangues)
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder={t('box.addLanguages')}
                                  />
                                )}
                              />
                            </FieldWrapper>
                            <FieldWrapper>
                              <Autocomplete
                                disableClearable
                                value={niveauLanguesList[lng.niveau - 1]}
                                sx={{ width: '100%', minWidth: 0, height: 45 }}
                                onChange={(event, newValue) => {
                                  const newLangues = [...langues]
                                  newLangues[index].niveau = newValue.id
                                  setLangues(newLangues)
                                  setRefresh(Math.random())
                                }}
                                isOptionEqualToValue={(option, value) =>
                                  option.titre[i18n.language] === value.titre
                                }
                                getOptionLabel={(option) =>
                                  option.titre[i18n.language]
                                }
                                options={niveauLanguesList}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    sx={{ backgroundColor: '#fff' }}
                                    InputProps={{
                                      ...params.InputProps,
                                    }}
                                  />
                                )}
                              />
                            </FieldWrapper>
                            <DeleteIconButton
                              onClick={(e) => {
                                e.preventDefault()
                                const newLangues = [...langues]
                                newLangues.splice(index, 1)
                                setLangues(newLangues)
                                setRefresh(Math.random())
                              }}
                              aria-label={t('button.delete')}
                            >
                              <FontAwesomeIcon icon={faTrash} fontSize={16} />
                            </DeleteIconButton>
                          </ListRow>
                        ))}
                        <AddRowButton onClick={handleAddLangue}>
                          <FontAwesomeIcon
                            icon={faSquarePlus}
                            fontSize={18}
                            color={'#4b5563'}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t('box.addOtherLanguage')}
                          </Typography>
                        </AddRowButton>
                      </Box>
                    </Grid>
                  </Grid>
                </FormCardContent>
              </FormCard>

              {/* Step 4: Publishing */}
              <FormCard sx={{ display: step === 5 ? 'block' : 'none' }}>
                <FormCardHeader>{t('box.step4Title')}</FormCardHeader>
                <FormCardContent>
                  {besoin &&
                  besoin.companySubscription &&
                  besoin.companySubscription.status === 'payer' &&
                  besoin.postuler ? (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4} key="company-subscription">
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: '20px',
                            border: '1px solid',
                            borderColor: 'primary.main',
                            background:
                              'linear-gradient(135deg, #e3f2fd, #ffffff)',
                            boxShadow: '0 10px 25px rgba(25, 118, 210, 0.2)',
                            transform: 'scale(1.02)',
                            transition: 'all 0.25s ease',
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faCircleCheck}
                            style={{
                              position: 'absolute',
                              top: 12,
                              right: 12,
                              color: '#1976d2',
                              fontSize: '20px',
                            }}
                          />
                          <Grid item xs={12} md={12}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="h6" fontWeight={700}>
                                {i18n.language === 'fr'
                                  ? besoin.companySubscription.subscriptionPlan
                                      .name
                                  : besoin.companySubscription.subscriptionPlan
                                      .nameEn}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={12}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                {i18n.language === 'fr'
                                  ? besoin.companySubscription.subscriptionPlan
                                      .description
                                  : besoin.companySubscription.subscriptionPlan
                                      .descriptionEn}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={12}>
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 600 }}
                              >
                                <FontAwesomeIcon
                                  icon={faCalendarAlt}
                                  style={{ marginRight: 8, color: '#667eea' }}
                                />
                                {besoin.dateExpiration
                                  ? moment(besoin.dateExpiration).format(
                                      'DD/MM/YYYY'
                                    )
                                  : 'N/A'}
                              </Typography>
                            </Box>
                          </Grid>
                          {besoin.companySubscription.subscriptionPlan.type !==
                            'subscription' && (
                            <Grid item xs={12} md={12}>
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 1, fontWeight: 600 }}
                                >
                                  <FontAwesomeIcon
                                    icon={faClock}
                                    style={{ marginRight: 8, color: '#667eea' }}
                                  />
                                  {
                                    besoin.companySubscription.subscriptionPlan
                                      .periodDays
                                  }{' '}
                                  {t('box.days')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          <Grid item xs={12} md={12}>
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="h4"
                                fontWeight={800}
                                color="primary.main"
                              >
                                {besoin.companySubscription.credits}{' '}
                                {t('dashboard.credits')}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={12}>
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  textTransform: 'capitalize',
                                }}
                              >
                                {t('profile.status')}:{' '}
                                {besoin.companySubscription.status}
                              </Typography>
                            </Box>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={3}>
                      {entrepriseOffres.map((entrepriseOffre) => {
                        const isSelected =
                          offerSelect?.id === entrepriseOffre.id
                        const isDisabled =
                          entrepriseOffre.credits === 0 ||
                          entrepriseOffre.status !== 'payer'
                        return (
                          <Grid item xs={12} md={4} key={entrepriseOffre.id}>
                            <Box
                              onClick={() => {
                                if (isDisabled) return

                                setOfferSelect(
                                  offerSelect?.id === entrepriseOffre.id
                                    ? null
                                    : entrepriseOffre
                                )

                                if (offerSelect?.endDate?.date) {
                                  setDateExpiration(
                                    moment(offerSelect.endDate.date)
                                      .tz('Europe/Paris')
                                      .format('DD/MM/YYYY')
                                  )
                                }
                              }}
                              sx={{
                                position: 'relative',
                                p: 3,
                                borderRadius: '20px',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                border: '1px solid',
                                borderColor: isSelected
                                  ? 'primary.main'
                                  : 'grey.200',
                                background: isSelected
                                  ? 'linear-gradient(135deg, #e3f2fd, #ffffff)'
                                  : '#fff',
                                boxShadow: isSelected
                                  ? '0 10px 25px rgba(25, 118, 210, 0.2)'
                                  : '0 5px 15px rgba(0,0,0,0.05)',
                                transform: isSelected
                                  ? 'scale(1.02)'
                                  : 'scale(1)',
                                transition: 'all 0.25s ease',
                                opacity: isDisabled ? 0.6 : 1,
                                '&:hover': {
                                  transform: isDisabled
                                    ? 'none'
                                    : 'translateY(-5px)',
                                  boxShadow: isDisabled
                                    ? 'none'
                                    : '0 10px 25px rgba(0,0,0,0.1)',
                                },
                              }}
                            >
                              {/* Selected Icon */}
                              {isSelected && (
                                <FontAwesomeIcon
                                  icon={faCircleCheck}
                                  style={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    color: '#1976d2',
                                    fontSize: '20px',
                                  }}
                                />
                              )}

                              {/* Disabled Icon */}
                              {isDisabled && (
                                <FontAwesomeIcon
                                  icon={faBan}
                                  style={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    color: '#d32f2f',
                                    fontSize: '20px',
                                  }}
                                />
                              )}

                              {/* Title */}
                              <Grid item xs={12} md={12}>
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="h6" fontWeight={700}>
                                    {entrepriseOffre.subscriptionName}
                                  </Typography>
                                </Box>
                              </Grid>

                              <Grid item xs={12} md={12}>
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ mb: 1, fontWeight: 600 }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faCalendarAlt}
                                      style={{
                                        marginRight: 8,
                                        color: '#667eea',
                                      }}
                                    />
                                    {moment(entrepriseOffre.startDate.date)
                                      .tz('Europe/Paris')
                                      .format('DD/MM/YYYY')}
                                  </Typography>
                                </Box>
                              </Grid>

                              <Grid item xs={12} md={12}>
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ mb: 1, fontWeight: 600 }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faClock}
                                      style={{
                                        marginRight: 8,
                                        color: '#667eea',
                                      }}
                                    />
                                    {entrepriseOffre.subscriptionType ===
                                    'one_shot'
                                      ? entrepriseOffre.periodDays +
                                        ' ' +
                                        t('box.days')
                                      : moment(entrepriseOffre.endDate.date)
                                          .tz('Europe/Paris')
                                          .format('DD/MM/YYYY')}
                                  </Typography>
                                </Box>
                              </Grid>

                              {/* Credits */}
                              <Grid item xs={12} md={12}>
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="h4"
                                    fontWeight={800}
                                    color={
                                      isDisabled
                                        ? 'text.disabled'
                                        : 'primary.main'
                                    }
                                  >
                                    {entrepriseOffre.credits}{' '}
                                    {t('dashboard.annoncesDisponibles')}
                                  </Typography>
                                </Box>
                              </Grid>

                              {/* Footer */}
                              {/* <Box sx={{ mt: 3 }}>
                          {isDisabled ? (
                            <Typography color="error" variant="body2">
                              {i18n.language === 'fr'
                                ? 'Crédits épuisés'
                                : 'No credits left'}
                            </Typography>
                          ) : isSelected ? (
                            <Typography color="primary" fontWeight={600}>
                              {i18n.language === 'fr'
                                ? 'Sélectionné'
                                : 'Selected'}
                            </Typography>
                          ) : (
                            <Typography variant="body2" >
                              {i18n.language === 'fr'
                                ? 'Cliquez pour sélectionner'
                                : 'Click to select'}
                            </Typography>
                          )}
                        </Box> */}
                            </Box>
                          </Grid>
                        )
                      })}
                    </Grid>
                  )}
                </FormCardContent>
              </FormCard>

              {/* Step 5: Preview */}
              <FormCard sx={{ display: step === 4 ? 'block' : 'none' }}>
                <FormCardHeader>{t('box.step5Title')}</FormCardHeader>
                <FormCardContent sx={{ p: { xs: 0, sm: 3 } }}>
                  <Grid container spacing={4}>
                    {/* Main content */}
                    <Grid item xs={12} md={8}>
                      <StyledCard>
                        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                          {/* Job header */}
                          <Box sx={{ mb: 4 }}>
                            <Typography
                              variant="h3"
                              component="h1"
                              sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                lineHeight: 1.2,
                                color: '#1a1a1a',
                                mb: 2,
                              }}
                            >
                              {posteSelect?.titre || t('box.posteTitle')}
                            </Typography>

                            <Box
                              sx={{
                                display: 'flex',
                                gap: 2,
                                mb: 2,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 3,
                                  py: 1,
                                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                  borderRadius: '20px',
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 600,
                                    color: '#667eea',
                                    fontSize: '1rem',
                                  }}
                                >
                                  {userData?.data?.entreprise?.nom ||
                                    t('contact.company')}
                                </Typography>
                              </Box>

                              {watch('nbrPoste') && (
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 3,
                                    py: 1,
                                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                    borderRadius: '20px',
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 600,
                                      color: '#10b981',
                                      fontSize: '1rem',
                                    }}
                                  >
                                    {watch('nbrPoste')}{' '}
                                    {watch('nbrPoste') > 1
                                      ? t('filter.poste') + 's'
                                      : t('filter.poste')}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>

                          {/* Job description */}
                          <Box sx={{ mb: 4 }}>
                            <TitleContainer>
                              <TitleBox>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faBriefcase}
                                    color="#667eea"
                                    fontSize={16}
                                  />
                                  {t('box.jobDescription')}
                                </Box>
                              </TitleBox>
                            </TitleContainer>
                            <Box
                              sx={{
                                backgroundColor: '#f8f9fa',
                                padding: 3,
                                borderRadius: '12px',
                                border: '1px solid #e9ecef',
                              }}
                            >
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: editorHtml || t('box.noDescription'),
                                }}
                                style={{
                                  fontSize: '1rem',
                                  lineHeight: 1.6,
                                  color: '#495057',
                                }}
                              />
                            </Box>
                          </Box>

                          {/* Skills */}
                          {competences && competences.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                              <TitleContainer>
                                <TitleBox>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faBrain}
                                      color="#667eea"
                                      fontSize={16}
                                    />
                                    {t('filter.competences')}
                                  </Box>
                                </TitleBox>
                              </TitleContainer>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                }}
                              >
                                {competences.map((skill, index) => (
                                  <Chip
                                    key={index}
                                    label={skill}
                                    sx={{
                                      margin: '4px',
                                      backgroundColor:
                                        'rgba(99, 102, 241, 0.08)',
                                      border:
                                        '1px solid rgba(99, 102, 241, 0.2)',
                                      color: '#6366f1',
                                      fontWeight: 600,
                                      fontSize: '0.875rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Languages */}
                          {langues && langues.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                              <TitleContainer>
                                <TitleBox>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faLanguage}
                                      color="#667eea"
                                      fontSize={16}
                                    />
                                    {t('filter.languages')}
                                  </Box>
                                </TitleBox>
                              </TitleContainer>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                }}
                              >
                                {langues.map((lang, index) => (
                                  <LanguageChip
                                    key={index}
                                    label={lang.langue}
                                    size={sizeChip}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Additional Skills */}
                          {competencesAnnexes &&
                            competencesAnnexes.length > 0 && (
                              <Box sx={{ mb: 4 }}>
                                <TitleContainer>
                                  <TitleBox>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={faChartLine}
                                        color="#667eea"
                                        fontSize={16}
                                      />
                                      {t('filter.competencesAnnexes')}
                                    </Box>
                                  </TitleBox>
                                </TitleContainer>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                  }}
                                >
                                  {competencesAnnexes.map((skill, index) => (
                                    <SkillChip
                                      key={index}
                                      label={skill}
                                      size={sizeChip}
                                      sx={{
                                        backgroundColor:
                                          'rgba(245, 158, 11, 0.08)',
                                        border:
                                          '1px solid rgba(245, 158, 11, 0.2)',
                                        color: '#f59e0b',
                                        '&:hover': {
                                          backgroundColor:
                                            'rgba(245, 158, 11, 0.12)',
                                        },
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}

                          {/* Soft Skills */}
                          {softskils && softskils.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                              <TitleContainer>
                                <TitleBox>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faBrain}
                                      color="#667eea"
                                      fontSize={16}
                                    />
                                    {t('filter.softskils')}
                                  </Box>
                                </TitleBox>
                              </TitleContainer>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                }}
                              >
                                {softskils.map((skill, index) => (
                                  <SoftSkillChip
                                    key={index}
                                    label={skill}
                                    size={sizeChip}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Values */}
                          {valeurs && valeurs.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                              <TitleContainer>
                                <TitleBox>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faHandshake}
                                      color="#667eea"
                                      fontSize={16}
                                    />
                                    {t('filter.values')}
                                  </Box>
                                </TitleBox>
                              </TitleContainer>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                }}
                              >
                                {valeurs.map((value, index) => (
                                  <ValueChip
                                    key={index}
                                    label={value}
                                    size={sizeChip}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Benefits */}
                          {avantagesEntreprise &&
                            avantagesEntreprise.length > 0 && (
                              <Box sx={{ mb: 4 }}>
                                <TitleContainer>
                                  <TitleBox>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={faHeart}
                                        color="#667eea"
                                        fontSize={16}
                                      />
                                      {t('filter.benefits')}
                                    </Box>
                                  </TitleBox>
                                </TitleContainer>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                  }}
                                >
                                  {avantagesEntreprise.map((benefit, index) => (
                                    <Box
                                      key={index}
                                      sx={{
                                        px: 2,
                                        py: 1,
                                        backgroundColor:
                                          'rgba(16, 185, 129, 0.1)',
                                        border:
                                          '1px solid rgba(16, 185, 129, 0.2)',
                                        borderRadius: '8px',
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: '#245343',
                                          fontWeight: 500,
                                        }}
                                      >
                                        {benefit}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}
                        </CardContent>
                      </StyledCard>
                    </Grid>

                    {/* Sidebar */}
                    <Grid item xs={12} md={4}>
                      {/* Job details card */}
                      <StyledCard>
                        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              mb: 3,
                              color: '#1a1a1a',
                            }}
                          >
                            {t('box.title.detailJob')}
                          </Typography>

                          {/* Location */}
                          <InfoBox>
                            <FontAwesomeIcon
                              icon={faMapMarkerAlt}
                              style={{
                                color: '#667eea',
                                fontSize: '18px',
                                marginRight: '12px',
                                minWidth: '24px',
                              }}
                            />
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {selectedCity || t('box.specifyLocation')}
                            </Typography>
                          </InfoBox>

                          {/* Contract type */}
                          <InfoBox>
                            <FontAwesomeIcon
                              icon={faBuilding}
                              style={{
                                color: '#10b981',
                                fontSize: '18px',
                                marginRight: '12px',
                                minWidth: '24px',
                              }}
                            />
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {typeContratSelect || '---'}
                            </Typography>
                          </InfoBox>

                          {/* Salary */}
                          {selectedSalaireSur && (
                            <InfoBox>
                              <FontAwesomeIcon
                                icon={faEuroSign}
                                style={{
                                  color: '#fbbf24',
                                  fontSize: '18px',
                                  marginRight: '12px',
                                  minWidth: '24px',
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {`${watch('salaire')}`}{' '}
                                {typeDeviseSelect?.toUpperCase() || 'CHF'}/
                                {t('box.mounth')}
                              </Typography>
                            </InfoBox>
                          )}

                          {/* Posted date */}
                          <InfoBox>
                            <FontAwesomeIcon
                              icon={faCalendarAlt}
                              style={{
                                color: '#8b5cf6',
                                fontSize: '18px',
                                marginRight: '12px',
                                minWidth: '24px',
                              }}
                            />
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {t('jobOffers.postedOn')}{' '}
                              {new Date(dateValue).toLocaleDateString()}
                            </Typography>
                          </InfoBox>

                          {/* Work hours */}
                          {/* {watch('horaires') && (
                        <InfoBox>
                          <FontAwesomeIcon
                            icon={faClock}
                            style={{
                              color: '#f59e0b',
                              fontSize: '18px',
                              marginRight: '12px',
                              minWidth: '24px',
                            }}
                          />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            <strong>{t('jobOffers.hours')}:</strong>{' '}
                            {watch('horaires')}
                          </Typography>
                        </InfoBox>
                      )} */}

                          {/* Remote work */}
                          {watch('teletravailPropose') !== undefined && (
                            <InfoBox>
                              <FontAwesomeIcon
                                icon={faUserTie}
                                style={{
                                  color: '#06b6d4',
                                  fontSize: '18px',
                                  marginRight: '12px',
                                  minWidth: '24px',
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                <strong>{t('jobOffers.remoteWork')}:</strong>{' '}
                                {`${watch('teletravailPropose')} ${t(
                                  'box.days'
                                )}`}
                              </Typography>
                            </InfoBox>
                          )}

                          {/* Work type */}
                          {selectedTypeTempsTravail && (
                            <InfoBox>
                              <FontAwesomeIcon
                                icon={faClock}
                                style={{
                                  color: '#10b981',
                                  fontSize: '18px',
                                  marginRight: '12px',
                                  minWidth: '24px',
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                <strong>{t('box.timeWorkType')}:</strong>{' '}
                                {selectedTypeTempsTravail === 'plein'
                                  ? t('box.timeWorkType1')
                                  : t('box.timeWorkType2')}
                              </Typography>
                            </InfoBox>
                          )}

                          {/* Direct report */}
                          {watch('reportDirect') && (
                            <InfoBox>
                              <FontAwesomeIcon
                                icon={faUserCheck}
                                style={{
                                  color: '#8b5cf6',
                                  fontSize: '18px',
                                  marginRight: '12px',
                                  minWidth: '24px',
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                <strong>{t('box.jobReport')}:</strong>{' '}
                                {watch('reportDirect')}
                              </Typography>
                            </InfoBox>
                          )}

                          {/* Travel requirements */}
                          {(deplacementValue !== undefined ||
                            permisValue !== undefined ||
                            vehiculeValue !== undefined) && (
                            <InfoBox>
                              <FontAwesomeIcon
                                icon={faRoad}
                                style={{
                                  color: '#f59e0b',
                                  fontSize: '18px',
                                  marginRight: '12px',
                                  minWidth: '24px',
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                <strong>{t('jobOffers.requirements')}:</strong>
                                <Box component="span" sx={{ ml: 1 }}>
                                  {deplacementValue && (
                                    <Box component="span" sx={{ mr: 1 }}>
                                      Déplacements requis
                                    </Box>
                                  )}
                                  {permisValue && (
                                    <Box component="span" sx={{ mr: 1 }}>
                                      Permis requis
                                    </Box>
                                  )}
                                  {vehiculeValue && (
                                    <Box component="span" sx={{ mr: 1 }}>
                                      Véhicule requis
                                    </Box>
                                  )}
                                  {!deplacementValue &&
                                    !permisValue &&
                                    !vehiculeValue &&
                                    t('jobOffers.noRequirements')}
                                </Box>
                              </Typography>
                            </InfoBox>
                          )}

                          {/* Job type */}
                          {selectedTypeValue && (
                            <InfoBox>
                              <FontAwesomeIcon
                                icon={faBriefcase}
                                style={{
                                  color: '#667eea',
                                  fontSize: '18px',
                                  marginRight: '12px',
                                  minWidth: '24px',
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                <strong>{t('box.jobPositionType')}:</strong>{' '}
                                {selectedTypeValue}
                              </Typography>
                            </InfoBox>
                          )}

                          {/* Country */}
                          {selectedCountry && (
                            <InfoBox>
                              <FontAwesomeIcon
                                icon={faMapMarkerAlt}
                                style={{
                                  color: '#ef4444',
                                  fontSize: '18px',
                                  marginRight: '12px',
                                  minWidth: '24px',
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                <strong>{t('contact.country')}:</strong>{' '}
                                {selectedCountry.label}
                              </Typography>
                            </InfoBox>
                          )}
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  </Grid>
                </FormCardContent>
              </FormCard>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mt: 4,
                  p: { xs: 2, sm: 3 },
                  backgroundColor: lightTheme.palette.primary.b6,
                  borderRadius: 2,
                  position: 'sticky',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 100,
                  gap: 2,
                }}
              >
                <OutlineButton
                  onClick={
                    step === 1
                      ? () => navigate(-1)
                      : step !== 5
                      ? previousStep
                      : firstStep
                  }
                  fullWidth
                  startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                  sx={{
                    minWidth: { xs: '100%', sm: 140 },
                    width: { xs: '100%', sm: 'auto' },
                    height: 45,
                    fontSize: 14,
                    fontFamily: 'Century Gothic Bold',
                    borderRadius: '12px',
                  }}
                >
                  {step === 1 ? t('button.cancel') : t('button.back')}
                </OutlineButton>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'flex-end',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 2,
                    width: '100%',
                  }}
                >
                  {step !== 5 && (
                    <OutlineButton
                      onClick={handleSuggesstion}
                      fullWidth
                      sx={{
                        minWidth: { xs: '100%', sm: 140 },
                        width: { xs: '100%', sm: 'auto' },
                        height: 45,
                        fontSize: 14,
                        fontFamily: 'Century Gothic Bold',
                        borderRadius: '12px',
                        color: lightTheme.palette.primary.main,
                        borderColor: lightTheme.palette.primary.b4,
                        '&:hover': {
                          backgroundColor: lightTheme.palette.primary.b5,
                        },
                      }}
                    >
                      {loadingSuggestion ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      🤖 Suggestion IA
                    </OutlineButton>
                  )}

                  {step !== 5 && (
                    <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      {loading ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: 45,
                          }}
                        >
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <GradientButton
                          onClick={
                            step < 3
                              ? handleSubmit(onSubmit1)
                              : step === 3
                              ? handleSubmit(onSubmit1)
                              : step === 4
                              ? handleSubmit(onSubmit)
                              : step === 5
                              ? handleSubmitPublish
                              : console.log('')
                          }
                          fullWidth
                          endIcon={
                            <FontAwesomeIcon
                              icon={
                                step < 3
                                  ? faArrowRight
                                  : step === 3
                                  ? faEye
                                  : faSave
                              }
                            />
                          }
                          sx={{
                            minWidth: { xs: '100%', sm: 140 },
                            width: { xs: '100%', sm: 'auto' },
                            height: 45,
                            fontSize: 14,
                            fontFamily: 'Century Gothic Bold',
                            borderRadius: '12px',
                          }}
                        >
                          {step < 3
                            ? t('button.next')
                            : step === 3
                            ? t('button.preview')
                            : step === 5
                            ? t('button.publishNow')
                            : t('button.save')}
                        </GradientButton>
                      )}
                    </Box>
                  )}

                  {besoin && (
                    <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      {loadingPublish ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: 45,
                          }}
                        >
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <GradientButton
                          onClick={
                            step === 5 ? handleSubmitPublish : handlePublish
                          }
                          disabled={step === 5 && !offerSelect}
                          fullWidth
                          endIcon={
                            step === 5 ? (
                              <FontAwesomeIcon icon={faSave} />
                            ) : null
                          }
                          sx={{
                            minWidth: { xs: '100%', sm: 140 },
                            width: { xs: '100%', sm: 'auto' },
                            height: 45,
                            fontSize: 14,
                            fontFamily: 'Century Gothic Bold',
                            borderRadius: '12px',
                            background:
                              'linear-gradient(135deg, #10b981 0%, #047857 100%)',
                            '&:hover': {
                              background:
                                'linear-gradient(135deg, #0f766e 0%, #065f46 100%)',
                            },
                          }}
                        >
                          {step === 5
                            ? t('button.publishNow')
                            : t('button.publish')}
                        </GradientButton>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </form>
          </>
        )}
      </ContentContainer>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        closeButton={false}
        transition={Slide}
        className={'toast'}
      />
    </PageContainer>
  )
}

export default EditBesoinEntreprisePage
